import dayjs from 'dayjs'
import { isDesktop } from 'react-device-detect'

import { isNotNullish } from '@local/utils'

import {
  BizItemType,
  BookingStatus,
  OrderStatusCode,
  UpcomingSection_UpcomingBookingFragment,
  UpcomingSection_UpcomingOrderFragment,
  UpcomingSection_UpcomingTrainTicketFragment,
} from '@web/federation/gql/graphql'
import { getDisplayVisitDate } from '@web/libs/getDisplayVisitDate'
import { TIMELINE_TIME_FORMAT } from '@web/pages/timeline/components/TimelineListOfDay/TimelineListOfDay.converter'
import {
  BookingCardItemType,
  OrderCardItemType,
  TicketCardItemType,
  UpcomingSectionItemType,
} from '@web/ui/pages/home/components/Sections/UIUpComingSection'

export const isDisplayDirectionBizItemType = (bizItemType: BizItemType) =>
  bizItemType === BizItemType.Pickup || bizItemType === BizItemType.Drivethru

export const convertUpcomingOrderItem = (upcomingOrder: UpcomingSection_UpcomingOrderFragment): OrderCardItemType => {
  const { bizItemType, placeSummary, directionLandingUrl, landingUrl } = upcomingOrder

  const carDirectionUrl = placeSummary?.directionLandingUrl?.car ?? directionLandingUrl?.car

  const isCanceled = upcomingOrder.orderStatusCode === OrderStatusCode.OrderCancelled

  return {
    ...upcomingOrder,
    progressStates: upcomingOrder.orderStates.filter(isNotNullish),
    stateIndex: upcomingOrder.orderStateIndex,
    isHighlight: !isCanceled,
    menuCount: Math.max(upcomingOrder.menuCount - 1, 0),
    type: 'order',
    displayOrderTimestamp: upcomingOrder.displayOrderTimestamp,
    orderedDateTimeText: upcomingOrder.formattedBookingDateText,
    landingUrl: landingUrl ? `${landingUrl}?from=myp` : undefined,
    coordinate: placeSummary?.coordinate ?? undefined,
    placeId: placeSummary?.id ?? undefined,
    carDirectionUrl: isDisplayDirectionBizItemType(bizItemType) ? carDirectionUrl : undefined,
    placeName: placeSummary?.name ?? undefined,
    ...(isCanceled && { isCanceled, isBlackBadge: true, stateIndex: -1 }),
  }
}

export const convertUpcomingBookingItem = (
  upcomingBooking: UpcomingSection_UpcomingBookingFragment,
): BookingCardItemType => {
  const {
    formattedBookingDateText,
    price,
    bizItemThumbImage,
    landingUrl,
    bookingStatusCode,
    placeSummary,
    directionLandingUrl,
    remainDays,
  } = upcomingBooking

  const carDirectionUrl = placeSummary?.directionLandingUrl?.car ?? directionLandingUrl?.car

  return {
    ...upcomingBooking,
    type: 'booking',
    isBlackBadge: bookingStatusCode === BookingStatus.Rc02,
    bookingDateTime: formattedBookingDateText,
    price: price ?? undefined,
    bizItemThumbImage: bizItemThumbImage ?? undefined,
    displayOrderTimestamp: upcomingBooking.displayOrderTimestamp,
    landingUrl: landingUrl ? `${landingUrl}?from=myp` : undefined,
    coordinate: placeSummary?.coordinate ?? undefined,
    placeId: placeSummary?.id ?? undefined,
    placeName: placeSummary?.name ?? undefined,
    carDirectionUrl,
    ...(isNotNullish(remainDays) && { isHighlight: remainDays <= 0 && bookingStatusCode === BookingStatus.Rc03 }),
  }
}

export const getDday = (date: string) => {
  return dayjs().startOf('day').diff(date, 'day')
}

export const convertUpcomingTrainTicketItem = (
  upcomingTrainTicket: UpcomingSection_UpcomingTrainTicketFragment,
): TicketCardItemType => {
  const dday = getDday(upcomingTrainTicket.journey.journeyAt)

  const {
    ticketInfoText,
    journey: { journeyAt, firstTrain, secondTrain },
  } = upcomingTrainTicket

  const origin = firstTrain.departureStop.name
  const stopOver = secondTrain ? secondTrain.departureStop.name : undefined
  const destination = secondTrain ? secondTrain.arrivalStop.name : firstTrain.arrivalStop.name

  return {
    id: upcomingTrainTicket.id,
    bookingDateTime: getDisplayVisitDate(journeyAt, false, TIMELINE_TIME_FORMAT).displayDateTime,
    origin,
    stopOver,
    destination,
    seatInfo: ticketInfoText,
    trainType: firstTrain.name,
    label: dday === 0 ? '오늘' : `D${dday}`,
    isToday: dday === 0,
    type: 'ticket',
    displayOrderTimestamp: upcomingTrainTicket.displayOrderTimestamp,
    href: (isDesktop ? upcomingTrainTicket.landingUrl.pcWebUrl : upcomingTrainTicket.landingUrl.webUrl) ?? '',
  }
}

export const sortUpcomingItems = (upcomingItems: UpcomingSectionItemType[]): UpcomingSectionItemType[] => {
  return upcomingItems.sort((a, b) => {
    /**
     * 주문은 입고일시 상관없이 최우선 노출
     */
    if (a.type === 'order') return -1

    if (b.type === 'order') return -1

    /**
     * 예약 / 승차권은 `displayOrderTimestamp` 기준 오름차순
     */

    if (dayjs(a.displayOrderTimestamp).isAfter(b.displayOrderTimestamp)) return 1

    if (dayjs(a.displayOrderTimestamp).isBefore(b.displayOrderTimestamp)) return -1

    return 0
  })
}
